import type { DuplicateCandidate, LeadForm, OfflineDraft, OfflineDraftStatus } from './types';

const databaseName = 'leadflow-offline-queue';
const storeName = 'drafts';

function draftId() {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(storeName)) database.createObjectStore(storeName, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('无法打开本机离线存储。'));
  });
}

async function requestStore<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = action(transaction.objectStore(storeName));
    let result: T;
    request.onsuccess = () => { result = request.result; };
    request.onerror = () => reject(request.error || new Error('本机离线存储操作失败。'));
    transaction.oncomplete = () => { database.close(); resolve(result); };
    transaction.onabort = () => { database.close(); reject(transaction.error || new Error('本机离线存储操作未完成。')); };
    transaction.onerror = () => { database.close(); reject(transaction.error || new Error('本机离线存储操作失败。')); };
  });
}

export async function listOfflineDrafts(): Promise<OfflineDraft[]> {
  const drafts = await requestStore<OfflineDraft[]>('readonly', (store) => store.getAll() as IDBRequest<OfflineDraft[]>);
  return drafts.map((draft) => ({
    ...draft,
    updatedAt: draft.updatedAt || draft.createdAt,
    ocrComplete: Boolean(draft.ocrComplete),
    duplicateCandidates: draft.duplicateCandidates || [],
  })).sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export async function queueOfflineDraft(form: LeadForm, image?: File | null): Promise<OfflineDraft> {
  const timestamp = new Date().toISOString();
  const draft: OfflineDraft = {
    id: draftId(),
    createdAt: timestamp,
    updatedAt: timestamp,
    form: { ...form },
    image: image || undefined,
    imageName: image?.name,
    imageType: image?.type,
    ocrComplete: false,
    status: 'queued',
    error: null,
    duplicateCandidates: [],
  };
  await requestStore<IDBValidKey>('readwrite', (store) => store.put(draft));
  return draft;
}

type DraftPatch = Partial<Pick<OfflineDraft, 'form' | 'status' | 'error' | 'duplicateCandidates' | 'image' | 'imageName' | 'imageType' | 'ocrComplete'>>;

export async function updateOfflineDraft(id: string, patch: DraftPatch): Promise<OfflineDraft | null> {
  const drafts = await listOfflineDrafts();
  const current = drafts.find((draft) => draft.id === id);
  if (!current) return null;
  const next: OfflineDraft = { ...current, ...patch, updatedAt: new Date().toISOString() };
  await requestStore<IDBValidKey>('readwrite', (store) => store.put(next));
  return next;
}

export async function recoverInterruptedDrafts(maxSyncAgeMs = 2 * 60 * 1000): Promise<number> {
  const now = Date.now();
  const drafts = await listOfflineDrafts();
  const interrupted = drafts.filter((draft) => draft.status === 'syncing' && (!Date.parse(draft.updatedAt) || now - Date.parse(draft.updatedAt) > maxSyncAgeMs));
  await Promise.all(interrupted.map((draft) => updateOfflineDraft(draft.id, {
    status: 'queued',
    error: '上一次补传被中断，已恢复为可重试状态。',
  })));
  return interrupted.length;
}

export async function removeOfflineDraft(id: string): Promise<void> {
  await requestStore<undefined>('readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export function draftImageFile(draft: OfflineDraft): File | null {
  if (!draft.image) return null;
  return new File([draft.image], draft.imageName || 'business-card.jpg', { type: draft.imageType || draft.image.type || 'image/jpeg' });
}

export function statusLabel(status: OfflineDraftStatus) {
  if (status === 'queued') return '等待补传';
  if (status === 'syncing') return '正在补传';
  if (status === 'review') return '等待确认';
  return '补传失败';
}

export function candidateSummary(candidates: DuplicateCandidate[]) {
  return candidates.map((candidate) => candidate.name || candidate.company || '未命名客户').join('、');
}
